<template>
  <div class="supplier-profile">
    <LoadingBar v-if="loading" message="Loading Profile..." />
    <div v-else-if="loadError" class="profile-error">{{ loadError }}</div>

    <template v-else>
        <header class="profile-banner">
  <div class="profile-banner__inner">
    <div class="profile-banner__identity">
      <div class="profile-banner__logo">
        <img v-if="profile.logoUrl" :src="getFileUrl(profile.logoUrl)" alt="Company logo" />
        <span v-else>{{ initials }}</span>
      </div>

      <div class="profile-banner__text">
        <div class="profile-banner__badges">
          <span v-for="t in profile.businessTypes" :key="t.id || t.type" class="profile-badge">
            {{ typeLabel(t.type) }}
            <span v-html="getStatusBadge(t.approval_status)" class="profile-badge__status"></span>
          </span>
        </div>
        <h1 class="profile-banner__name">{{ profile.company_name }}</h1>
        <p class="profile-banner__meta">
          {{ profile.street_city }}, {{ profile.state }} · Member since {{ profile.memberSince }}
        </p>
      </div>
    </div>

    <div class="profile-banner__actions">
      <router-link :to="{ name: 'EditProfile' }" class="profile-btn profile-btn--ghost">Edit Profile</router-link>
      <button 
        v-if="showApprovalButton" 
        class="profile-btn profile-btn--primary" 
        @click="submitForApproval"
        :disabled="submittingApproval"
      >
        {{ submittingApproval ? 'Submitting...' : 'Send for Approval' }}
      </button>
    </div>
  </div>
  <div v-if="showPendingNotice" class="profile-banner__notice">
    <span class="profile-banner__notice-icon">⏳</span>
    <div>
      <strong>Your information has been submitted for review.</strong>
      <span>We're reviewing your application and will update you soon.</span>
    </div>
  </div>
  <div v-if="hasRejectedType && !hasApprovedType" class="profile-banner__notice profile-banner__notice--warning">
    <span class="profile-banner__notice-icon">⚠️</span>
    <div>
      <strong>Your application was not approved.</strong>
      <span>Please review the feedback, update your profile, and resubmit for approval.</span>
    </div>
  </div>
<div v-if="showProfileIncompleteNotice" class="profile-banner__notice profile-banner__notice--warning">
  <span class="profile-banner__notice-icon">⚠️</span>
  <div>
    <strong>Ready to submit for approval?</strong>
    <span>Make sure all your information is complete. You can update any details using the <strong>Edit Profile</strong> button above, then click Send for Approval when you're ready.</span>
  </div>
</div>
  <!-- Success notice after submission -->
  <div v-if="showApprovalSuccess" class="profile-banner__notice profile-banner__notice--success">
    <span class="profile-banner__notice-icon">✅</span>
    <div>
      <strong>Profile submitted for approval!</strong>
      <span>Your information has been sent for review. You will be notified once approved.</span>
    </div>
  </div>
</header>
          <!-- ============ Tabs ============ -->
    <div class="profile-tabs-wrapper">
      <nav class="profile-tabs">
        <button
          type="button"
          class="profile-tab"
          :class="{ 'is-active': activeTab === 'info' }"
          @click="activeTab = 'info'"
        >
          <i class="fa fa-user profile-tab__icon"></i>
          My Information
        </button>
        <button
          type="button"
          class="profile-tab"
          :class="{ 'is-active': activeTab === 'suppliers' }"
          @click="activeTab = 'suppliers'; loadMySuppliers()"
        >
          <i class="fa fa-building profile-tab__icon"></i>
          My Network
          <span v-if="pendingReceivedCount" class="profile-tab__badge">{{ pendingReceivedCount }}</span>
        </button>
        <button
          type="button"
          class="profile-tab"
          :class="{ 'is-active': activeTab === 'reviews' }"
          @click="activeTab = 'reviews'; loadMyReviews()"
        >
          <i class="fa fa-star profile-tab__icon"></i>
          My Reviews
          <span v-if="pendingReviewsCount" class="profile-tab__badge">{{ pendingReviewsCount }}</span>
        </button>
        <span class="profile-tabs__indicator" :style="indicatorStyle"></span>
      </nav>
  </div>
      <div v-if="activeTab === 'info'" class="profile-container">
        <!-- ============ Quick stats ============ -->
        <div class="profile-stats">
          <div class="profile-stat">
            <strong>{{ profile.materials.length }}</strong>
            <span>Materials Listed</span>
          </div>
          <div class="profile-stat">
            <strong>{{ certificationCount }}</strong>
            <span>Certifications</span>
          </div>
          <div class="profile-stat">
            <strong>{{ profile.services.length }}</strong>
            <span>Services Offered</span>
          </div>
          <div class="profile-stat">
            <strong>{{ complianceYesCount }}/{{ profile.compliance.length }}</strong>
            <span>Compliance Items Met</span>
          </div>
        </div>
<!-- ============ Business Types ============ -->
<section class="profile-section">
  <div class="section-head">
    <span class="section-head__icon">🏷️</span>
    <div>
      <h4 class="section-head__title">Business Types</h4>
      <p class="section-head__desc">
        Registered business categories and their approval status.
      </p>
    </div>
  </div>

  <div class="business-types-grid">
    <div
      v-for="type in profile.businessTypes"
      :key="type.id"
      class="business-type-card"
    >
      <div class="business-type-card__header">
        <h5>{{ typeLabel(type.type) }}</h5>

        <span
          v-html="getStatusBadge(type.approval_status)"
          class="business-type-card__status"
        ></span>
      </div>

      <div class="business-type-card__body"  v-if="type.description">
        <div class="info-row">
          <span class="info-value">
           Remarks: <p class="text-sm text-muted">{{ type.description || '.' }}</p>
          </span>
        </div>

        <div class="info-row"  v-if="type.approved_at">
          <span class="info-value">
            {{ formatReviewDate(type.approved_at )|| 'Not reviewed yet' }}
          </span>
        </div>
      </div>
    </div>
  </div>
</section>
        <!-- ============ Company Information ============ -->
        <section class="profile-section">
          <div class="section-head">
            <span class="section-head__icon">🏢</span>
            <div>
              <h4 class="section-head__title">Company Information</h4>
              <p class="section-head__desc">Contact and location details on file.</p>
            </div>
          </div>
          <div class="info-grid">
            <div class="info-item"><span class="info-label">Email</span><span class="info-value">{{ profile.email
                }}</span></div>
            <div class="info-item"><span class="info-label">Phone</span><span class="info-value">{{ profile.phone
                }}</span></div>
            <div class="info-item"><span class="info-label">Address</span><span class="info-value">{{
              profile.physical_address }}</span></div>
            <div class="info-item"><span class="info-label">City</span><span class="info-value">{{ profile.street_city
                }}</span></div>
            <div class="info-item"><span class="info-label">State / Province</span><span class="info-value">{{
              profile.state }}</span></div>
            <div class="info-item"><span class="info-label">Postal Code</span><span class="info-value">{{
              profile.postal_code }}</span></div>
          </div>
        </section>
        <!-- ============ Manufacturing (manufacturer type) ============ -->
        <section v-if="profile.businessTypes.includes('manufacturer')" class="profile-section">
          <div class="section-head">
            <span class="section-head__icon">🏭</span>
            <div>
              <h4 class="section-head__title">Manufacturing</h4>
              <p class="section-head__desc">How goods are produced.</p>
            </div>
          </div>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Manufacturing Type</span>
              <span class="info-value">{{ ucfirst(profile.manufacturing_site) }}</span>
            </div>
            <div v-if="profile.manufacturing_type" class="info-item">
              <span class="info-label">Manufacturing Site</span>
              <span class="info-value">{{ ucfirst(profile.manufacturing_type) }}</span>
            </div>
            <div v-if="profile.product_brand_type" class="info-item">
              <span class="info-label">Manufacturer Category</span>
              <span class="info-value">
                <span class="profile-badge profile-badge--alert">{{
                  manufacturerCategoryLabel(profile.product_brand_type) }}</span>
              </span>
            </div>
          </div>
        </section>


        <!-- ============ Primary Point of Contact ============ -->
        <section class="profile-section">
          <div class="section-head">
            <span class="section-head__icon">👤</span>
            <div>
              <h4 class="section-head__title">Primary Point of Contact</h4>
              <p class="section-head__desc">Who to reach out to first.</p>
            </div>
          </div>
          <div class="info-grid">
            <div class="info-item"><span class="info-label">POC Name</span><span class="info-value">{{ profile.poc
                }}</span></div>
            <div class="info-item"><span class="info-label">Title</span><span class="info-value">{{ profile.title
                }}</span></div>
            <div class="info-item"><span class="info-label">Emergency Contact</span><span class="info-value">{{
              profile.emergency_contact }}</span></div>
          </div>
        </section>

        <!-- ============ QA Contact ============ -->
        <section class="profile-section">
          <div class="section-head">
            <span class="section-head__icon">🔬</span>
            <div>
              <h4 class="section-head__title">QA Contact</h4>
              <p class="section-head__desc">Who handles quality and compliance questions.</p>
            </div>
          </div>
          <div class="info-grid">
            <div class="info-item"><span class="info-label">QA Name</span><span class="info-value">{{ profile.qa_name
                }}</span></div>
            <div class="info-item"><span class="info-label">QA Title</span><span class="info-value">{{ profile.qa_title
                }}</span></div>
            <div class="info-item"><span class="info-label">QA Email</span><span class="info-value">{{ profile.qa_email
                }}</span></div>
            <div class="info-item"><span class="info-label">QA Phone</span><span class="info-value">{{ profile.qa_phone
                }}</span></div>
          </div>
        </section>

        <!-- ============ Regulatory Compliance ============ -->
        <section class="profile-section">
          <div class="section-head">
            <span class="section-head__icon">📋</span>
            <div>
              <h4 class="section-head__title">Regulatory Compliance</h4>
              <p class="section-head__desc">Answers on file for the compliance questionnaire.</p>
            </div>
          </div>
          <table class="data-table">
            <thead>
              <tr>
                <th class="data-table__num">#</th>
                <th>Question</th>
                <th class="data-table__answer">Answer</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(q, i) in profile.compliance" :key="q.key">
                <td class="data-table__num">{{ i + 1 }}</td>
                <td>{{ q.text }}</td>
                <td class="data-table__answer">
                  <span class="yn-badge" :class="ynClass(q.answer)">{{ ynIcon(q.answer) }} {{ q.answer }}</span>
                </td>
                <td class="data-table__muted  asp-cell-wrap">{{ q.remarks || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <!-- ============ Identity Preserved Certifications ============ -->
        <section v-if="profile.ipCertOption === 'yes'" class="profile-section">
          <div class="section-head">
            <span class="section-head__icon">🌱</span>
            <div>
              <h4 class="section-head__title">Identity Preserved Certifications</h4>
              <p class="section-head__desc">Standards and claims maintained.</p>
            </div>
          </div>
          <table class="data-table">
            <thead>
              <tr>
                <th>Certification</th>
                <th>File</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in profile.ipCerts" :key="c.key">
                <td>{{ c.icon }} {{ c.label }}</td>
                <td>
                  <a v-if="c.fileName" :href="getFileUrl(c.file)" class="data-table__link">{{ c.fileName }}</a>
                  <span v-else class="data-table__muted">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        <!-- ============ Food Safety Certification ============ -->
        <section class="profile-section">
          <div class="section-head">
            <span class="section-head__icon">🛡️</span>
            <div>
              <h4 class="section-head__title">Food Safety Certification</h4>
              <p class="section-head__desc">
                {{ profile.foodSafetyCert === 'yes' ? 'Accredited certifications on file.' : 'Non-certified questionnaire on file.' }}
              </p>
            </div>
          </div>

          <table v-if="profile.foodSafetyCert === 'yes'" class="data-table">
            <thead>
              <tr>
                <th>Certificate</th>
                <th>Expiry</th>
                <th>Comment</th>
                <th>File</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="cert in profile.certificates" :key="cert.id">
                <td class="data-table__strong">{{ cert.title }}</td>
                <td class="data-table__muted">{{ cert.expiryDate || '—' }}</td>
                <td class="data-table__muted">{{ cert.comment || '—' }}</td>
                <td>
                  <a v-if="cert.fileName" :href="getFileUrl(cert.file)" class="data-table__link">{{ cert.fileName }}</a>
                  <span v-else class="data-table__muted">—</span>
                </td>
              </tr>
            </tbody>
          </table>

          <table v-else class="data-table">
            <thead>
              <tr>
                <th>Question</th>
                <th class="data-table__answer">Answer</th>
                <th>Details</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="q in profile.nonCert" :key="q.key">
                <td>{{ q.text }}</td>
                <td class="data-table__answer">
                  <span class="yn-badge" :class="ynClass(q.answer)">
                    {{ ynIcon(q.answer) }} {{ q.answer }}
                  </span>
                </td>
                <td class="data-table__details">
                  <template v-if="q.details && typeof q.details === 'object'">
                    <div v-for="(value, key) in q.details" :key="key" class="detail-item">
                      <strong>{{ key }}:</strong>
                      <span>{{ value }}</span>
                    </div>
                  </template>

                  <template v-else-if="q.details">
                    {{ q.details }}
                  </template>

                  <template v-else>
                    —
                  </template>
                </td>
                <td class="data-table__muted  asp-cell-wrap">{{ q.remarks || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <!-- ============ Services offered ============ -->
        <section v-if="profile.services.length" class="profile-section">
          <div class="section-head">
            <span class="section-head__icon">🤝</span>
            <div>
              <h4 class="section-head__title">Services Offered</h4>
              <p class="section-head__desc">Categories of service this account provides.</p>
            </div>
          </div>
          <div class="chip-row">
            <span v-for="s in profile.services" :key="s.id" class="chip">{{ s.label }}</span>
          </div>
          <p v-if="profile.services_description" class="profile-note">{{ profile.services_description }}</p>
        </section>

        <!-- ============ Materials ============ -->
        <section v-if="profile.materials.length" class="profile-section">
          <div class="section-head">
            <span class="section-head__icon">📦</span>
            <div>
              <h4 class="section-head__title">Materials</h4>
              <p class="section-head__desc">Materials supplied or produced.</p>
            </div>
          </div>
          <table class="data-table">
            <thead>
              <tr>
                <th>Sr.</th>
                <th>Name</th>
                <th>Type</th>
                <th>Packaging</th>
                <th>Lead Time</th>
                <th>Allergens</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(material, index) in profile.materials" :key="material.id">
                <td class="data-table__strong">{{ index + 1 }}</td>
                <td class="data-table__strong">{{ material.name }}</td>
                <td class="data-table__muted">{{ material.type }}</td>
                <td class="data-table__muted">{{ material.packaging_size }} · {{ material.packaging_type }}</td>
                <td class="data-table__muted">{{ material.lead_time }}</td>
                <td class="data-table__muted">{{ material.allergens }}</td>
                <td>
                  <button type="button" class="rform__btn rform__btn--sm" @click="openMaterialEditModal(material)">
                    ✏️ Edit
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </section>


        <FormModal ref="editMaterialModal" title="Edit Material" id="edit_material_form_modal"
          @close="closeEditMaterialModal">
          <ReusableForm id="material_edit_form" :loading="editSubmitting" submit-label="Save Changes"
            :show-cancel="false" @submit.prevent="submitMaterialEdit">
            <div class="rform__row mt-3">
              <div class="rform__field">
                <label class="rform__label rform__label--required">Name</label>
                <input v-model="editForm.name" type="text" class="rform__input" name="name" id="edit_material_name"
                  placeholder="e.g. Organic Cane Sugar" />
              </div>

              <div class="rform__field">
                <label class="rform__label rform__label--required">Type</label>
                <div class="rform__select-wrap">
                  <select v-model="editForm.type" class="rform__select" name="type" id="edit_material_type">
                    <option value="">Select type</option>
                    <option value="raw_material">Raw Material</option>
                    <option value="packaging">Packaging Material</option>
                    <option value="finished_material">Finished Material</option>
                  </select>
                </div>
              </div>

              <div class="rform__field">
                <label class="rform__label">Lead Time</label>
                <input v-model="editForm.lead_time" type="text" class="rform__input" name="lead_time"
                  id="edit_material_lead_time" placeholder="e.g. 4-6 weeks" />
              </div>
            </div>

            <div class="rform__row mt-3">
              <div class="rform__field">
                <label class="rform__label">Packaging Size</label>
                <input v-model="editForm.packaging_size" type="text" class="rform__input" name="packaging_size"
                  id="edit_material_packaging_size" placeholder="e.g. 25 kg bag" />
              </div>

              <div class="rform__field">
                <label class="rform__label">Packaging Type</label>
                <div class="rform__select-wrap">
                  <select v-model="editForm.packaging_type" class="rform__select" name="packaging_type"
                    id="edit_material_packaging_type">
                    <option value="">Select packaging type</option>
                    <option value="glass">Glass</option>
                    <option value="plastic">Plastic</option>
                    <option value="paper_box">Paper Box</option>
                    <option value="metal">Metal</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="row py-4 px-3">
              <div class="col-md-6">
                <div class="rform__field">
                  <label class="rform__label">Allergens List</label>
                  <MultiSelect v-model="editForm.allergens" :options="allergensOptions" :multiple="true"
                    option-label="name" option-value="id" placeholder="Select allergens..."
                    id="edit_material_allergens" />
                </div>
              </div>

              <div class="col-md-6">
                <div class="rform__field">
                  <label class="rform__label">Sub Ingredients</label>
                  <textarea v-model="editForm.sub_ingredients" class="rform__textarea" rows="3" name="sub_ingredients"
                    id="edit_material_sub_ingredients"></textarea>
                </div>
              </div>
            </div>
          </ReusableForm>
        </FormModal>

        <!-- ============ Products ============ -->
        <section v-if="profile.products && profile.products.length" class="profile-section">
          <div class="section-head">
            <span class="section-head__icon">📦</span>
            <div>
              <h4 class="section-head__title">Products</h4>
              <p class="section-head__desc">Products manufactured or offered.</p>
            </div>
          </div>

          <table class="data-table">
            <thead>
              <tr>
                <th>Sr.</th>
                <th>Product Name</th>
                <th>Production Type</th>
                <th>Unit</th>
                <th>SKU</th>
                <th>Application</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(product, index) in profile.products" :key="product.id">
                <td class="data-table__strong">{{ index + 1 }}</td>
                <td class="data-table__strong">{{ product.name }}</td>
                <td class="data-table__muted">{{ product.production_type }}</td>
                <td class="data-table__muted">{{ product.unit }}</td>
                <td class="data-table__muted">{{ product.sku }}</td>
                <td class="data-table__muted">{{ product.application }}</td>
                <td>
                  <button type="button" class="rform__btn rform__btn--sm" @click="openProductEditModal(product)">
                    ✏️ Edit
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        <!-- ============ Edit Product Modal ============ -->
        <FormModal ref="editProductModal" title="Edit Product" id="edit_product_form_modal"
          @close="closeEditProductModal">
          <ReusableForm id="product_edit_form" :loading="editProductSubmitting" submit-label="Save Changes"
            :show-cancel="false" @submit.prevent="submitProductEdit">
            <div class="rform__row mt-3">
              <div class="rform__field">
                <label class="rform__label rform__label--required">Name</label>
                <input v-model="editProductForm.name" type="text" class="rform__input" name="name"
                  id="edit_product_name" placeholder="e.g. Organic Trail Mix" />
              </div>

              <div class="rform__field">
                <label class="rform__label">Product Application</label>
                <div class="rform__select-wrap">
                  <select v-model="editProductForm.application" class="rform__select" name="application"
                    id="edit_product_application">
                    <option value="">Select application type</option>
                    <option value="retail">Retail</option>
                    <option value="wholesale">Wholesale</option>
                    <option value="food_service">Food Service</option>
                    <option value="institutional_sale">Institutional Sale</option>
                  </select>
                </div>
              </div>

              <div class="rform__field">
                <label class="rform__label">Product Code / SKU</label>
                <input v-model="editProductForm.sku" type="text" class="rform__input" name="sku" id="edit_product_sku"
                  placeholder="e.g. TM-1024" />
              </div>
            </div>

            <div class="rform__row mt-3">
              <div class="rform__field">
                <label class="rform__label rform__label--required">Production Type</label>
                <div class="rform__select-wrap">
                  <select v-model="editProductForm.production_type" class="rform__select" name="production_type"
                    id="edit_product_production_type">
                    <option value="">--Select--</option>
                    <option value="wip">WIP (Work In Progress)</option>
                    <option value="fp">FP (Finish Product)</option>
                  </select>
                </div>
              </div>

              <div class="rform__field">
                <label class="rform__label rform__label--required">Unit</label>
                <input v-model="editProductForm.unit" type="text" class="rform__input" name="unit"
                  id="edit_product_unit" placeholder="e.g. kg, box, case" />
              </div>
            </div>

            <div class="rform__row mt-3">
              <div class="rform__field" style="grid-column: 1 / -1;">
                <label class="rform__label">Product Description</label>
                <textarea v-model="editProductForm.description" class="rform__textarea" rows="2"
                  placeholder="Optional add any details about this product..." name="description"
                  id="edit_product_description"></textarea>
              </div>
            </div>
          </ReusableForm>
        </FormModal>
        <!-- ============ Additional notes ============ -->
        <section v-if="profile.comments" class="profile-section">
          <div class="section-head">
            <span class="section-head__icon">💬</span>
            <div>
              <h4 class="section-head__title">Additional Notes</h4>
            </div>
          </div>
          <p class="profile-note">{{ profile.comments }}</p>
        </section>
      </div>
    <!-- ============ My Suppliers tab ============ -->
<div v-else-if="activeTab === 'suppliers'" class="profile-container">
  <section class="profile-section">
    <div class="section-head">
      <span class="section-head__icon">📥</span>
      <div>
    <h4 class="section-head__title">Added You to Their Network</h4>
    <p class="section-head__desc">Members who have added you to their business network.</p>
</div>
    </div>

    <p v-if="loadingMySuppliers" class="profile-note">Loading…</p>
    <p v-else-if="!mySuppliersReceived.length" class="profile-note">No requests yet.</p>

    <div v-else class="supplier-row-list">
      <div v-for="row in mySuppliersReceived" :key="row.id" class="supplier-row">
        <div class="supplier-row__identity">
          <div class="supplier-row__avatar">
            <img v-if="row.supplier?.photo" :src="getFileUrl(row.supplier.photo)" alt="" />
            <span v-else>{{ rowInitials(row.supplier?.company_name) }}</span>
          </div>
          <div>
            <span class="supplier-row__name">{{ row.supplier?.company_name || 'Unknown supplier' }}</span>
            <!-- Add Reviews Component -->
            <SupplierReviews
              v-if="row.supplier?.id"
              :supplier-id="row.supplier.id"
              mode="compact"
              :show-review-button="true"
              :show-write-button="true"
            />
          </div>
        </div>

        <div class="supplier-row__actions">
          <div class="supplier-row__status">
            <!-- Status badges... -->
          </div>
          <router-link
            :to="{ name: 'SupplierProfile', params: { external_id: row.supplier?.external_id } }"
            class="rform__btn rform__btn--sm"
          >
            <i class="fa fa-eye"></i> Profile
          </router-link>
        </div>
      </div>
    </div>
  </section>

  <section class="profile-section">
    <div class="section-head">
      <span class="section-head__icon">📤</span>
      <div>
   <h4 class="section-head__title">Your Connections</h4>
<p class="section-head__desc">Members you’ve connected with through your network.</p>
      </div>
    </div>

    <p v-if="loadingMySuppliers" class="profile-note">Loading…</p>
    <p v-else-if="!mySuppliersSent.length" class="profile-note">You haven't requested any suppliers yet.</p>

    <div v-else class="supplier-row-list">
      <div v-for="row in mySuppliersSent" :key="row.id" class="supplier-row">
        <div class="supplier-row__identity">
          <div class="supplier-row__avatar">
            <img v-if="row.target_supplier?.photo" :src="getFileUrl(row.target_supplier.photo)" alt="" />
            <span v-else>{{ rowInitials(row.target_supplier?.company_name) }}</span>
          </div>
          <div>
            <span class="supplier-row__name">{{ row.target_supplier?.company_name || 'Unknown supplier' }}</span>
            <!-- Add Reviews Component -->
            <SupplierReviews
              v-if="row.target_supplier?.id"
              :supplier-id="row.target_supplier.id"
              mode="compact"
              :show-review-button="true"
              :show-write-button="true"
            />
          </div>
        </div>
        <div class="supplier-row__actions">
          <router-link
            :to="{ name: 'SupplierProfile', params: { external_id: row.target_supplier?.external_id } }"
            class="rform__btn rform__btn--sm"
          >
            <i class="fa fa-eye"></i> Profile
          </router-link>
        </div>
      </div>
    </div>
  </section>
</div>
<!-- ============ My Reviews tab ============ -->
<div v-else-if="activeTab === 'reviews'" class="profile-container">
  <section class="profile-section">
    <div class="section-head">
      <span class="section-head__icon">⭐</span>
      <div>
        <h4 class="section-head__title">Reviews About You</h4>
        <p class="section-head__desc">Reviews left by other suppliers about your company.</p>
      </div>
    </div>

    <!-- Stats Summary -->
    <div v-if="myReviews.length > 0" class="reviews-stats-summary">
      <div class="reviews-stats-summary__item">
        <span class="reviews-stats-summary__number">{{ myReviews.length }}</span>
        <span class="reviews-stats-summary__label">Total Reviews</span>
      </div>
      <div class="reviews-stats-summary__item">
        <span class="reviews-stats-summary__number">{{ myAverageRating }}</span>
        <span class="reviews-stats-summary__label">Average Rating</span>
      </div>
      <div class="reviews-stats-summary__item">
        <span class="reviews-stats-summary__stars">
          <span v-for="n in 5" :key="n" class="star" :class="{ 'star--filled': n <= Math.round(myAverageRating) }">★</span>
        </span>
        <span class="reviews-stats-summary__label">Rating</span>
      </div>
    </div>

    <p v-if="loadingMyReviews" class="profile-note">Loading reviews…</p>
    <p v-else-if="!myReviews.length" class="profile-note">No reviews yet.</p>

    <!-- Reviews List -->
    <div v-else class="reviews-list">
      <div 
        v-for="review in myReviews" 
        :key="review.id" 
        class="review-card"
      >
        <div class="review-card__header">
          <div class="review-card__identity">
            <div class="review-card__avatar">
              <img v-if="review.reviewer?.photo" :src="getFileUrl(review.reviewer.photo)" alt="" />
              <span v-else>{{ getReviewerInitials(review.reviewer) }}</span>
            </div>
            <div>
              <strong class="review-card__name">{{ getReviewerName(review.reviewer) }}</strong>
              <span class="review-card__date">{{ formatReviewDate(review.created_at) }}</span>
            </div>
          </div>
          <div class="review-card__stars">
            <span 
              v-for="n in 5" 
              :key="n" 
              class="star"
              :class="{ 'star--filled': n <= review.stars }"
            >
              ★
            </span>
          </div>
        </div>
        
        <p v-if="review.review" class="review-card__content">{{ review.review }}</p>
        <p v-if="review.comments" class="review-card__comments">{{ review.comments }}</p>
        
       
        <div class="review-card__footer">
          <span
            class="review-card__status"
            :class="{
              'review-card__status--approved': review.status === 'approved',
              'review-card__status--pending': review.status === 'pending',
              'review-card__status--rejected': review.status === 'rejected',
              'review-card__status--reported': review.status === 'reported'
            }"
          >
            {{ review.status || 'Pending' }}
          </span>
 
          <button
            v-if="review.status !== 'reported'"
            type="button"
            class="review-card__report-btn"
            @click="toggleReportForm(review.id)"
          >
            <i class="fa fa-flag"></i> Report Abuse
          </button>
          <span v-else class="review-card__reported-label">
            <i class="fa fa-flag"></i> Reported
          </span>
        </div>
 
        <!-- Inline report form — only open for the review being reported -->
        <div v-if="reportingReviewId === review.id" class="report-abuse-form">
          <label class="rform__label rform__label--required">Reason for reporting</label>
          <textarea
            v-model="reportReason"
            class="rform__textarea"
            rows="3"
            placeholder="Explain why this review should be reviewed by our team..."
          ></textarea>
          <span v-if="reportError" class="rform__error">{{ reportError }}</span>
 
          <div class="report-abuse-form__files">
            <label class="rform__label">Attach evidence (optional)</label>
            <input
              type="file"
              multiple
              class="rform__input"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              @change="onReportFilesSelected"
            />
            <div v-if="reportFiles.length" class="report-abuse-form__file-chips">
              <span v-for="(f, i) in reportFiles" :key="i" class="chip">
                {{ f.name }}
                <button type="button" aria-label="Remove file" @click="removeReportFile(i)">✕</button>
              </span>
            </div>
          </div>
 
          <div class="report-abuse-form__actions">
            <button type="button" class="rform__btn rform__btn--ghost" @click="cancelReportForm">Cancel</button>
            <button
              type="button"
              class="rform__btn rform__btn--primary"
              :disabled="submittingReport"
              @click="submitReport(review)"
            >
              {{ submittingReport ? 'Submitting…' : 'Submit Report' }}
            </button>
          </div>
        </div>
 
      </div>
    </div>
  </section>
</div>
    </template>
  </div>
</template>
<script>
import { form_api_submit } from '@/utils/FormSubmit'
import { resolveFileUrl as resolveAssetFileUrl } from '@/utils/fileUrl'
import LoadingBar from '@/components/utils/LoadingBar.vue'
import MultiSelect from '@/components/MultiSelect.vue'
import FormModal from '@/components/utils/FormModal.vue'
import ReusableForm from '@/components/utils/Form.vue'
import SupplierReviews from '@/components/SupplierReviews.vue'
export default {

  name: 'SupplierProfile',
  components: {
    LoadingBar,
    MultiSelect,
    FormModal,
    ReusableForm,
    SupplierReviews,
  },
  data() {
    return {
      loading: true,
      loadError: '',
      profile: null,

      // Tabs
      activeTab: 'info',
      currentSupplierId: localStorage.getItem('supplier_id'),

      // My Suppliers tab
      mySuppliersSent: [],
      mySuppliersReceived: [],
      loadingMySuppliers: false,
      updatingStatusId: null,

      // Material Edit
      editingMaterial: null,
      editingMaterialId: null,
      savingMaterial: false,
      editSubmitting: false,
      editMaterialModal: null,

      // Allergens
      allergens: [],
      allergensOptions: [],

      // Edit Form
      editForm: {
        name: '',
        type: '',
        packaging_size: '',
        packaging_type: '',
        lead_time: '',
        sub_ingredients: '',
        allergens: [],
      },
      editingProductId: null,
      editProductSubmitting: false,
      editProductForm: {
        name: '',
        application: '',
        sku: '',
        production_type: '',
        unit: '',
        description: '',
      },
       myReviews: [],
    loadingMyReviews: false,
        submittingApproval: false,
    showApprovalSuccess: false,
    approvalSubmitted: false,
      reportingReviewId: null,
      reportReason: '',
      reportFiles: [],
      reportError: '',
      submittingReport: false,
    }
  },
  mounted() {
    const supplierId = localStorage.getItem('supplier_id')
    if (!supplierId) {
      this.loadError = 'No ID found. Please sign in again.'
      this.loading = false
      return
    }

    this.loadProfile()

    this.loadQues()
  },

  computed: {
    initials() {
      if (!this.profile) return ''
      return (this.profile.company_name || '')
        .split(' ')
        .map((w) => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    },
    certificationCount() {
      if (!this.profile) return 0
      const ip = this.profile.ipCertOption === 'yes' ? this.profile.ipCerts.length : 0
      const food = this.profile.foodSafetyCert === 'yes' ? this.profile.certificates.length : 0
      return ip + food
    },
    complianceYesCount() {
      if (!this.profile) return 0
      return this.profile.compliance.filter((q) => q.answer === 'YES').length
    },
     allTypesPending() {
    if (!this.profile || !this.profile.businessTypes || !this.profile.businessTypes.length) {
      return false;
    }
    return this.profile.businessTypes.every((t) => {
      const status = t.approval_status;
      return !status || status === 'pending' || status === 'in_progress';
    });
  },
    pendingReceivedCount() {
      return this.mySuppliersReceived.filter((r) => r.approval_status === 'pending').length
    },
     pendingReviewsCount() {
    return this.myReviews.filter((r) => r.status === 'pending').length
  },
  myAverageRating() {
    if (!this.myReviews.length) return 0
    const total = this.myReviews.reduce((sum, r) => sum + r.stars, 0)
    return (total / this.myReviews.length).toFixed(1)
  },

  
      
  hasApprovedType() {
    return this.profile.businessTypes.some((t) => t.approval_status === 'approved');
  },

  hasRejectedType() {
    return this.profile.businessTypes.some((t) => t.approval_status === 'rejected');
  },
  showApprovalButton() {
  return !this.approvalSubmitted;
},

showPendingNotice() {
  return (
    this.approvalSubmitted === true &&
    !this.profile.businessTypes.some(t => t.approval_status === 'rejected')
  );
},

showProfileIncompleteNotice() {
  return !this.approvalSubmitted;
}
  },

  methods: {
  getStatusBadge(status) {
  const effectiveStatus = status || 'pending';
  
  const map = {
    approved: {
      icon: '✔',
      title: 'Approved',
      bg: 'rgba(26,127,75,.12)',
      color: '#1a7f4b',
      border: 'rgba(26,127,75,.3)',
    },
    pending: {
      icon: '⏳',
      title: 'Pending',
      bg: 'rgba(236,153,58,.14)',
      color: '#b5670f',
      border: 'rgba(236,153,58,.35)',
    },
    in_progress: {
      icon: '⏳',
      title: 'In Progress',
      bg: 'rgba(236,153,58,.14)',
      color: '#b5670f',
      border: 'rgba(236,153,58,.35)',
    },
    partially_approved: {
      icon: '◐',
      title: 'Partially Approved',
      bg: 'rgba(33,150,243,.12)',
      color: '#1976d2',
      border: 'rgba(33,150,243,.3)',
    },
    rejected: {
      icon: '✕',
      title: 'Rejected',
      bg: 'rgba(179,38,30,.1)',
      color: '#b3261e',
      border: 'rgba(179,38,30,.3)',
    },
  }

  const s = map[effectiveStatus]
  if (!s) return ''

  return `
    <span
      title="${s.title}"
      style="
        display:inline-flex;
        align-items:center;
        justify-content:center;
        width:18px;
        height:18px;
        border-radius:50%;
        background:${s.bg};
        color:${s.color};
        border:1px solid ${s.border};
        font-size:11px;
        font-weight:700;
      "
    >
      ${s.icon}
    </span>
  `
},
    async loadMySuppliers() {

      if (!this.currentSupplierId) return

      this.loadingMySuppliers = true

      const result = await form_api_submit({
        url: 'my-suppliers/list',
        method: 'GET',
        data: { supplier_id: this.currentSupplierId },
        show_success_toast: false,
        show_error_toast: false,
      })

      if (result.success) {
        this.mySuppliersSent = result.data?.sent || []
        this.mySuppliersReceived = result.data?.received || []
      }

      this.loadingMySuppliers = false
    },
    async respondToRequest(row, approvalStatus) {
      this.updatingStatusId = row.id

      const result = await form_api_submit({
        url: `my-suppliers/${row.id}/updatestatus`,
        method: 'PATCH',
        data: {
          supplier_id: this.currentSupplierId,
          approval_status: approvalStatus,
        },
        show_success_toast: true,
        show_error_toast: true,
      })

      if (result.success) {
        row.approval_status = approvalStatus
      }

      this.updatingStatusId = null
    },

    rowInitials(name) {
      return (name || '')
        .split(' ')
        .map((w) => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    },

    async loadQues() {
      this.loadingConfig = true
      try {
        const result = await form_api_submit({
          url: 'supplier/create_supplier',
          method: 'GET',
          show_success_toast: false,
          show_error_toast: false,
        })

        if (result?.success) {
          const data = result.data || {}

          this.allergensOptions = data.allergens || []

        } else {
          this.topError = 'Could not load the form. Please refresh and try again.'
        }
      } catch (error) {
        this.topError = 'Could not load the form. Please refresh and try again.'
      } finally {
        this.loadingConfig = false
      }
    },

    ucfirst(str) {
      if (!str || typeof str !== 'string') return str || ''
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    manufacturerCategoryLabel(value) {
      const map = {
        private_label: 'Private Label',
        co_mfg: 'Co Manufacturer',
        external_brand: 'External Brand',
      }
      return map[value] || this.titleCaseFromSlug(value)
    },
    titleCaseFromSlug(str) {
      if (!str || typeof str !== 'string') return ''
      return str
        .split('_')
        .filter(Boolean)
        .map((word) => this.ucfirst(word))
        .join(' ')
    },
async loadProfile() {
  this.loading = true
  this.loadError = ''

  const result = await form_api_submit({
    url: 'supplier/my-profile',
    method: 'GET',
    show_success_toast: false,
    show_error_toast: true,
  })

  if (result.success) {
    this.profile = this.mapProfile(result.data)
    
    // Only use the API value, don't override it
    const supplier = result.data.supplier || {};
    this.approvalSubmitted = supplier.approval_submitted == 1; // Convert to boolean
    
  } else {
    this.loadError = 'Could not load your profile. Please try again.'
  }

  this.loading = false
},
    getFileUrl(filePath) {
      return resolveAssetFileUrl(filePath)
    },
    mapProfile(data) {
      const supplier = data.supplier || {}
      const supplierTypes = data.supplier_types || []
     const businessTypes = supplierTypes.map((t) => ({
        id: t.id,
        type: t.name || t.type,       // adjust to whichever field actually holds 'supplier' / 'manufacturer' etc.
        approval_status: t.approval_status,
        description: t.description || '',
        approved_at: t.approved_at || null,
      }))

      return {
         approval_submitted: supplier.approval_submitted || false,
        logoUrl: supplier.photo ? this.resolveFileUrl(supplier.photo) : '',
        businessTypes,
        memberSince: this.formatMemberSince(supplier.created_at),

        company_name: this.ucfirst(supplier.company_name || ''),
        email: supplier.email || '',
        phone: supplier.phone || '',
        physical_address: this.ucfirst(supplier.physical_address || ''),
        street_city: this.ucfirst(supplier.street_city || ''),
        state: this.ucfirst(supplier.state || ''),
        postal_code: supplier.postal_code || '',

        manufacturing_type: supplier.manufacture_type || '',
        manufacturing_site: supplier.manufacture_site || '',
        product_brand_type: supplier.manufacture_category || '',

        production_city: this.ucfirst(supplier.production_city || ''),
        production_state: this.ucfirst(supplier.production_state || ''),
        production_address: this.ucfirst(supplier.production_address || ''),
        production_postcode: supplier.production_postcode || '',

        services: (data.services || []).map((s) => ({
          id: s.id,
          label: this.ucfirst(s.name),
        })),
        services_description: supplier.services_description || '',

        poc: this.ucfirst(supplier.POC || supplier.poc || ''),
        title: this.ucfirst(supplier.title || ''),
        emergency_contact: supplier.emergency_contact || '',

        qa_name: this.ucfirst(supplier.qa_name || ''),
        qa_title: this.ucfirst(supplier.qa_title || ''),
        qa_email: supplier.qa_email || '',
        qa_phone: supplier.qa_phone || '',

        compliance: (data.questionnaire_responses || []).map((q) => ({
          key: q.question_key || q.id,
          text: q.question_text,
          answer: q.response,
          remarks: q.remarks,
        })),

        ipCertOption: supplier.ip_certificates || 'na',
        ipCerts: (data.ip_certificates || []).map((c) => ({
          key: c.id,
          icon: '🌱',
          label: this.ucfirst(c.document_name),
          file: c.document || '',
          fileName: c.document ? c.document.split('/').pop() : '',
        })),

        foodSafetyCert: supplier.food_safety_cert || 'no',
        certificates: (data.certificates || []).map((c) => ({
          id: c.id,
          title: this.ucfirst(c.document_name),
          expiryDate: c.expiry_date,
          comment: c.description,
          file: c.document || '',
          fileName: c.document ? c.document.split('/').pop() : '',
        })),

        nonCert: (data.non_cert_responses || []).map((q) => ({
          key: q.question_key || q.id,
          text: q.question_text,
          answer: q.response,
          details: q.sub_items_response
            ? (() => {
              try {
                return JSON.parse(q.sub_items_response);
              } catch (e) {
                return q.sub_items_response;
              }
            })()
            : null,
          remarks: q.remarks,
        })),
        materials: (data.materials || []).map((m) => ({
          id: m.id,
          name: this.ucfirst(m.name),
          type: this.titleCaseFromSlug(m.type),
          packaging_size: m.pack_size,
          lead_time: m.lead_time,
          sub_ingredients: m.sub_ingredients || '',
          packaging_type: this.titleCaseFromSlug(m.pack_type),
          allergens: (m.get_allergens || []).map((a) => this.ucfirst(a.name)).join(', ') || '—',
        })),
        products: (data.products || []).map((p) => ({
          id: p.id,
          name: this.ucfirst(p.name),
          type: this.titleCaseFromSlug(p.type),
          production_type: { rm: 'Raw Material', wip: 'Work In Progress', fp: 'Finished Product' }[p.production_type] || this.titleCaseFromSlug(p.production_type),
          unit: this.ucfirst(p.unit),
          sku: p.sku || '—',
          application: this.titleCaseFromSlug(p.application),
          description: p.description || '', // <-- add this
        })),
        comments: this.ucfirst(supplier.comments || ''),
      }
    },

    formatMemberSince(createdAt) {
      if (!createdAt) return ''
      const d = new Date(createdAt)
      if (Number.isNaN(d.getTime())) return ''
      return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    },

    resolveFileUrl(path) {
      // Adjust to match wherever your storage/CDN base URL lives (e.g. an env var).
      return `${this.$store?.state?.assetBaseUrl || ''}/${path}`
    },

    typeLabel(type) {
      const map = { supplier: 'Supplier', manufacturer: 'Manufacturer', service_provider: 'Service Provider' }
      return map[type] || type
    },
    ynClass(answer) {
      if (answer === 'YES') return 'yn-badge--yes'
      if (answer === 'NO') return 'yn-badge--no'
      return 'yn-badge--na'
    },
    ynIcon(answer) {
      if (answer === 'YES') return '✓'
      if (answer === 'NO') return '✕'
      return '—'
    },
      async submitForApproval() {
    this.submittingApproval = true;

    try {
      const result = await form_api_submit({
        url: 'supplier/submit-for-approval',
        method: 'POST',
        show_success_toast: true,
        show_error_toast: true,
        remove_pre_toasts: true,
      });

      if (result?.success) {
        this.showApprovalSuccess = true;
        this.approvalSubmitted = true;
        // Refresh profile data
        await this.loadProfile();
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          this.showApprovalSuccess = false;
        }, 5000);
      }
    } catch (error) {
      console.error('Error submitting for approval:', error);
    } finally {
      this.submittingApproval = false;
    }
  },
    openMaterialEditModal(material, ref = 'editMaterialModal') {
      const typeMap = {
        'Raw Material': 'raw_material',
        'Packaging Material': 'packaging',
        'Finished Material': 'finished_material',
      }

      const packagingTypeMap = {
        'Glass': 'glass',
        'Plastic': 'plastic',
        'Paper Box': 'paper_box',
        'Metal': 'metal',
      }

      const selectedAllergenNames = (material.allergens || '')
        .split(',')
        .map(a => a.trim().toLowerCase().replace(/_/g, ' '))
        .filter(Boolean)

      // Store IDS, not objects — matches option-value="id" contract
      const selectedAllergenIds = (this.allergensOptions || [])
        .filter(opt =>
          selectedAllergenNames.includes(
            (opt.name || opt.label || '').toLowerCase().replace(/_/g, ' ')
          )
        )
        .map(opt => opt.id)

      this.editingMaterialId = material.id

      this.editForm = {
        name: material.name || '',
        type: typeMap[material.type] || material.type || '',
        packaging_size: material.packaging_size || '',
        packaging_type: packagingTypeMap[material.packaging_type] || material.pack_type || '',
        lead_time: material.lead_time || '',
        sub_ingredients: material.sub_ingredients || '',
        allergens: selectedAllergenIds,
      }

      this.$nextTick(() => {
        this.$refs[ref].showModal()
      })
    },

    closeEditMaterialModal() {
      if (this.editMaterialModal) {
        this.editMaterialModal.hide()
      }

      this.editingMaterialId = null
    },


    async submitMaterialEdit() {
      this.editSubmitting = true

      try {
        const fd = new FormData()
        console.log(localStorage.getItem('supplier_token'))
        const scalars = ['name', 'type', 'packaging_size', 'lead_time', 'sub_ingredients']
        scalars.forEach(key => {
          if (this.editForm[key] !== null && this.editForm[key] !== '') {
            fd.append(key, this.editForm[key])
          }
        })

        // allergens is already an array of raw ids now
        this.editForm.allergens.forEach(id => fd.append('allergen_ids[]', id))


        const result = await form_api_submit({
          url: `supplier/materials/update/${this.editingMaterialId}`,
          method: 'POST',
          data: fd,
          curr_form_id: '#material_edit_form',
        })

        if (result.success) {
          // Update the row in profile.materials in place from the API response
          const idx = this.profile.materials.findIndex(m => m.id === this.editingMaterialId)
          if (idx !== -1) {
            const updated = result.data
            this.profile.materials.splice(idx, 1, {
              ...this.profile.materials[idx],
              name: updated.name,
              type: updated.type,
              packaging_size: updated.pack_size,
              lead_time: updated.lead_time,
              sub_ingredients: updated.sub_ingredients,
              allergens: (updated.get_allergens || []).map(a => a.name).join(', '),
            })
          }
          this.closeEditMaterialModal()
          this.loadProfile()
        }
      } finally {
        this.editSubmitting = false
      }
    },

    openProductEditModal(product, ref = 'editProductModal') {
      const applicationMap = {
        'Retail': 'retail',
        'Wholesale': 'wholesale',
        'Food Service': 'food_service',
        'Institutional Sale': 'institutional_sale',
      }
      const productionTypeMap = {
        'Raw Material': 'rm',
        'Work In Progress': 'wip',
        'Finished Product': 'fp',
      }

      this.editingProductId = product.id

      this.editProductForm = {
        name: product.name || '',
        application: applicationMap[product.application] || product.application || '',
        sku: product.sku && product.sku !== '—' ? product.sku : '',
        production_type: productionTypeMap[product.production_type] || product.production_type || '',
        unit: product.unit || '',
        description: product.description || '',
      }

      this.$nextTick(() => {
        this.$refs[ref].showModal()
      })
    },

    closeEditProductModal() {
      this.editingProductId = null
    },

    async submitProductEdit() {
      this.editProductSubmitting = true

      try {
        const fd = new FormData()
        const scalars = ['name', 'application', 'sku', 'production_type', 'unit', 'description']
        scalars.forEach(key => {
          if (this.editProductForm[key] !== null && this.editProductForm[key] !== '') {
            fd.append(key, this.editProductForm[key])
          }
        })

        const result = await form_api_submit({
          url: `supplier/products/update/${this.editingProductId}`,
          method: 'POST',
          data: fd,
          curr_form_id: '#product_edit_form',
        })

        if (result.success) {
          const idx = this.profile.products.findIndex(p => p.id === this.editingProductId)
          if (idx !== -1) {
            const updated = result.data
            this.profile.products.splice(idx, 1, {
              ...this.profile.products[idx],
              name: updated.name,
              production_type: {
                rm: 'Raw Material',
                wip: 'Work In Progress',
                fp: 'Finished Product',
              }[updated.production_type] || this.titleCaseFromSlug(updated.production_type),
              unit: updated.unit,
              sku: updated.sku || '—',
              application: this.titleCaseFromSlug(updated.application),
            })
          }
          this.closeEditProductModal()
          this.loadProfile()
        }
      } finally {
        this.editProductSubmitting = false
      }
    },
  async loadMyReviews() {
    if (!this.currentSupplierId) return
    
    this.loadingMyReviews = true
    
    const result = await form_api_submit({
      url: `supplier/${this.currentSupplierId}/reviews`,
      method: 'GET',
      show_success_toast: false,
      show_error_toast: false
    })
    
    this.loadingMyReviews = false
    
    if (result.success) {
      this.myReviews = result.data.reviews || []
    }
  },
  
  getReviewerInitials(reviewer) {
    if (!reviewer) return '?'
    const name = reviewer.company_name || `${reviewer.first_name || ''} ${reviewer.last_name || ''}`.trim()
    return name
      .split(' ')
      .map(w => w[0])
      .slice(0, 2)
      .join('')
      .toUpperCase()
  },
  
  getReviewerName(reviewer) {
    if (!reviewer) return 'Anonymous'
    return reviewer.company_name || `${reviewer.first_name || ''} ${reviewer.last_name || ''}`.trim() || 'Anonymous'
  },
  
  formatReviewDate(dateString) {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  },
  
    toggleReportForm(reviewId) {
      if (this.reportingReviewId === reviewId) {
        this.cancelReportForm()
        return
      }
      this.reportingReviewId = reviewId
      this.reportReason = ''
      this.reportFiles = []
      this.reportError = ''
    },
 
    cancelReportForm() {
      this.reportingReviewId = null
      this.reportReason = ''
      this.reportFiles = []
      this.reportError = ''
    },
 
    onReportFilesSelected(e) {
      this.reportFiles.push(...Array.from(e.target.files))
      e.target.value = ''
    },
 
    removeReportFile(idx) {
      this.reportFiles.splice(idx, 1)
    },
 
    async submitReport(review) {
      if (!this.reportReason.trim()) {
        this.reportError = 'Please explain why you are reporting this review.'
        return
      }
      this.reportError = ''
      this.submittingReport = true
 
      const fd = new FormData()
      fd.append('report_reason', this.reportReason.trim())
      this.reportFiles.forEach((file, i) => fd.append(`attachments[${i}]`, file))
 
      const result = await form_api_submit({
        url: `reviews/${review.id}/report`,
        method: 'POST',
        data: fd,
        show_success_toast: true,
        show_error_toast: true,
      })
 
      this.submittingReport = false
 
      if (result.success) {
        review.status = 'reported' // updates the badge + hides the button immediately
        this.cancelReportForm()
      }
    },
  },
}
</script>

<style scoped>
.supplier-profile {
  width: 100%;
  min-height: 100vh;
  background: var(--color-surface);
  font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  -webkit-font-smoothing: antialiased;
}

.supplier-profile * {
  box-sizing: border-box;
}

.profile-banner {
  background: var(--color-tint-light);
  border-bottom: 1px solid var(--color-border);
  padding: 22px 24px;
}

.profile-banner__inner {
  max-width: 1080px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
}

.profile-banner__identity {
  display: flex;
  align-items: center;
  gap: 16px;
}

.profile-banner__logo {
  width: 56px;
  height: 56px;
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  flex-shrink: 0;
  overflow: hidden;
}

.profile-banner__logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-banner__badges {
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.profile-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  font-size: 11px;
  font-weight: 600;
  color: var(--color-text-dark);
}

.profile-badge--verified {
  background: rgba(26, 127, 75, 0.08);
  border-color: rgba(26, 127, 75, 0.3);
  color: #1a7f4b;
}

.profile-banner__name {
  margin: 0 0 2px;
  font-size: 21px;
  font-weight: 800;
  color: var(--color-text-dark);
}

.profile-banner__meta {
  margin: 0;
  font-size: 12.5px;
  color: var(--color-text-muted);
}

.profile-banner__actions {
  flex-shrink: 0;
}

.profile-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 16px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.18s ease, border-color 0.18s ease, opacity 0.18s ease;
  border-radius: 8px;
  border: 1px solid transparent;
}

.profile-btn--ghost {
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-dark);
}

.profile-btn--ghost:hover {
  background: var(--color-tint-light);
  border-color: var(--color-secondary);
}

.profile-btn--approve {
  height: 32px;
  padding: 0 12px;
  font-size: 12px;
  background: rgba(26, 127, 75, 0.1);
  border-color: rgba(26, 127, 75, 0.3);
  color: #1a7f4b;
}
.profile-btn--approve:hover:not(:disabled) {
  background: rgba(26, 127, 75, 0.18);
}

.profile-btn--decline {
  height: 32px;
  padding: 0 12px;
  font-size: 12px;
  background: rgba(179, 38, 30, 0.06);
  border-color: rgba(179, 38, 30, 0.25);
  color: #b3261e;
}
.profile-btn--decline:hover:not(:disabled) {
  background: rgba(179, 38, 30, 0.12);
}

.profile-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ============ Tabs ============ */
.profile-tabs {
  max-width: 1080px;
  margin: 16px auto 0;
  padding: 0 24px;
  display: flex;
  gap: 4px;
  border-bottom: 1px solid var(--color-border);
}

.profile-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 4px;
  margin-right: 22px;
  border: none;
  background: transparent;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--color-text-muted);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: color 0.18s ease, border-color 0.18s ease;
}

.profile-tab:hover {
  color: var(--color-text-dark);
}

.profile-tab.is-active {
  color: var(--color-primary);
  border-color: var(--color-primary);
}

.profile-tab__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--color-primary);
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
}

/* ============ Supplier row list (My Suppliers tab) ============ */
.supplier-row-list {
  display: flex;
  flex-direction: column;
  gap: 1px;
  background: var(--color-border);
  border: 1px solid var(--color-border);
}

.supplier-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: var(--color-surface);
}

.supplier-row__identity {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.supplier-row__avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  overflow: hidden;
}

.supplier-row__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.supplier-row__name {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--color-text-dark);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.supplier-row__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.profile-container {
  max-width: 1080px;
  margin: 0 auto;
  padding: 22px 24px 56px;
}

.profile-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: var(--color-border);
  border: 1px solid var(--color-border);
  margin-bottom: 24px;
}

.profile-stat {
  background: var(--color-surface);
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.profile-stat strong {
  font-size: 18px;
  font-weight: 700;
  color: var(--color-primary);
}

.profile-stat span {
  font-size: 11px;
  color: var(--color-text-muted);
  font-weight: 500;
}

.profile-section {
  margin-bottom: 4px;
}

.section-head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 22px 0 12px;
  padding-top: 18px;
  border-top: 1px solid var(--color-border);
}

.profile-container>.profile-section:first-of-type .section-head {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}

.section-head__icon {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  background: var(--color-tint-light);
  display: grid;
  place-items: center;
  font-size: 14px;
}

.section-head__title {
  margin: 0 0 1px;
  font-size: 13.5px;
  font-weight: 700;
  color: var(--color-text-dark);
}

.section-head__desc {
  margin: 0;
  font-size: 11.5px;
  color: var(--color-text-muted);
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px 18px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--color-border);
}

.info-label {
  font-size: 10.5px;
  font-weight: 600;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.info-value {
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-dark);
}

.chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-dark);
}

.profile-note {
  margin: 8px 0 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--color-text-muted);
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
}

.data-table thead th {
  text-align: left;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--color-text-muted);
  padding: 0 10px 8px;
  border-bottom: 1px solid var(--color-border);
}

.data-table tbody td {
  padding: 9px 10px;
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text-dark);
  vertical-align: top;
}

.data-table tbody tr:last-child td {
  border-bottom: none;
}

.data-table tbody tr:hover td {
  background: var(--color-tint-light);
}

.data-table__num {
  width: 28px;
  color: var(--color-text-muted);
  font-weight: 600;
}

.data-table__answer {
  width: 90px;
}

.data-table__strong {
  font-weight: 700;
}

.data-table__muted {
  color: var(--color-text-muted);
}

.data-table__link {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-secondary);
  text-decoration: none;
}

.data-table__link:hover {
  text-decoration: underline;
}

.yn-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 22px;
  padding: 0 8px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
}

.yn-badge--yes {
  background: #1a7f4b;
}

.yn-badge--no {
  background: #b3261e;
}

.yn-badge--na {
  background: var(--color-text-muted);
}

@media (max-width: 900px) {
  .info-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 760px) {
  .profile-stats {
    grid-template-columns: repeat(2, 1fr);
  }

  .data-table {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
  }
}

@media (max-width: 480px) {
  .profile-banner__inner {
    flex-direction: column;
    align-items: flex-start;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .supplier-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .supplier-row__actions {
    width: 100%;
  }

  .profile-tabs {
    padding: 0 16px;
  }
}

.profile-badge--alert {
  background: rgba(236, 153, 58, 0.12);
  border-color: rgba(236, 153, 58, 0.35);
  color: #b5670f;
}

.detail-item {
  display: flex;
  flex-direction: column;
  margin-bottom: 6px;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-item strong {
  font-size: 0.75rem;
  color: #374151;
  font-weight: 600;
}

.detail-item span {
  color: #6b7280;
  word-break: break-word;
}
.profile-badge__status {
  margin-left: 6px;
}
.profile-banner__notice {
  max-width: 1080px;
  margin: 14px auto 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: rgba(236, 153, 58, 0.12);
  border: 1px solid rgba(236, 153, 58, 0.35);
  border-radius: 8px;
  font-size: 12.5px;
  color: #b5670f;
}
.profile-banner__notice-icon {
  font-size: 16px;
  flex-shrink: 0;
}
.profile-banner__notice strong {
  display: block;
  font-size: 13px;
  margin-bottom: 1px;
}
.profile-banner__notice span {
  color: #8c5410;
}
.profile-tabs-wrapper {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-bottom: 24px;
}

.profile-tabs {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px;
  border-radius: 14px;
  background: var(--color-tint-light);
  border: 1px solid var(--color-border);
  margin-bottom: 0;
}

.profile-tab {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 18px;
  border: none;
  background: transparent;
  border-radius: 10px;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--color-text-muted);
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.2s ease;
}

.profile-tab__icon {
  font-size: 12px;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.profile-tab.is-active {
  color: #fff;
}

.profile-tab.is-active .profile-tab__icon {
  opacity: 1;
}

/* Sliding pill behind the active tab */
.profile-tab::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 10px;
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  opacity: 0;
  transform: scale(0.94);
  transition: opacity 0.2s ease, transform 0.2s ease;
  z-index: -1;
}

.profile-tab.is-active::before {
  opacity: 1;
  transform: scale(1);
  box-shadow: 0 6px 16px -6px rgba(93, 28, 106, 0.45);
}

.profile-tab:hover:not(.is-active) {
  color: var(--color-text-dark);
}

.profile-tab__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #b3261e;
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
  line-height: 1;
}

.profile-tab.is-active .profile-tab__badge {
  background: rgba(255, 255, 255, 0.25);
}

@media (max-width: 480px) {
  .profile-tabs {
    width: 100%;
  }
  .profile-tab {
    flex: 1;
    justify-content: center;
    padding: 0 10px;
  }
}
.business-types-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
}

.business-type-card {
  border: 1px solid var(--color-border);
  border-radius: 14px;
  background: #fff;
  padding: 18px;
  transition: all .2s ease;
}

.business-type-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(0,0,0,.06);
}

.business-type-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}

.business-type-card__header h5 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--color-text-dark);
}

.business-type-card__body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: .05em;
}

.info-value {
  font-size: 14px;
  color: var(--color-text-dark);
  line-height: 1.5;
  word-break: break-word;
}

.business-type-card__status {
  flex-shrink: 0;
}
/* Add to your component's styles */
.supplier-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid var(--color-border);
  gap: 1rem;
  flex-wrap: wrap;
}

.supplier-row__identity {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 200px;
}

.supplier-row__identity > div {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.supplier-row__avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
  font-weight: 700;
  font-size: 16px;
}

.supplier-row__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.supplier-row__name {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-dark);
}

.supplier-row__actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}
.supplier-row .supplier-reviews--compact {
  display: inline-flex;
  align-items: center;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .supplier-row {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .supplier-row__actions {
    justify-content: flex-start;
  }
}
/* ============ My Reviews Tab ============ */
.reviews-stats-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: var(--color-border);
  border: 1px solid var(--color-border);
  margin-bottom: 24px;
}

.reviews-stats-summary__item {
  background: var(--color-surface);
  padding: 16px 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  text-align: center;
}

.reviews-stats-summary__number {
  font-size: 24px;
  font-weight: 700;
  color: var(--color-primary);
}

.reviews-stats-summary__stars {
  display: flex;
  gap: 2px;
  font-size: 20px;
  color: #d1d5db;
}

.reviews-stats-summary__stars .star--filled {
  color: #f59e0b;
}

.reviews-stats-summary__label {
  font-size: 11px;
  color: var(--color-text-muted);
  font-weight: 500;
}

.reviews-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.review-card {
  padding: 1rem;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  background: var(--color-surface);
  transition: box-shadow 0.2s;
}

.review-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.review-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  gap: 1rem;
  flex-wrap: wrap;
}

.review-card__identity {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.review-card__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
  overflow: hidden;
}

.review-card__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.review-card__name {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-dark);
}

.review-card__date {
  display: block;
  font-size: 11px;
  color: var(--color-text-muted);
}

.review-card__stars {
  display: flex;
  gap: 2px;
  font-size: 16px;
  color: #d1d5db;
  flex-shrink: 0;
}

.review-card__stars .star--filled {
  color: #f59e0b;
}

.review-card__content {
  margin: 0 0 0.5rem;
  font-size: 14px;
  line-height: 1.6;
  color: var(--color-text-dark);
  white-space: pre-wrap;
  word-break: break-word;
}

.review-card__comments {
  margin: 0 0 0.5rem;
  font-size: 13px;
  line-height: 1.6;
  color: var(--color-text-muted);
  font-style: italic;
}

.review-card__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--color-border);
}

.review-card__status {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
}

.review-card__status--approved {
  background: rgba(26, 127, 75, 0.12);
  color: #1a7f4b;
}

.review-card__status--pending {
  background: #fef3c7;
  color: #92400e;
}

.review-card__status--rejected {
  background: rgba(179, 38, 30, 0.1);
  color: #b3261e;
}

.review-card__id {
  font-size: 11px;
  color: var(--color-text-muted);
}

/* Responsive */
@media (max-width: 640px) {
  .reviews-stats-summary {
    grid-template-columns: 1fr;
  }
  
  .review-card__header {
    flex-direction: column;
    align-items: stretch;
  }
  
  .review-card__stars {
    align-self: flex-start;
  }
  
  .review-card__footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}
.profile-banner__notice--warning {
  background: #fef3c7;
  border-color: #f59e0b;
  color: #92400e;
}

.profile-banner__notice--success {
  background: #d1fae5;
  border-color: #10b981;
  color: #065f46;
}

.profile-btn--primary {
  background: linear-gradient(135deg, #0b1799, #1a2a6c);
  color: #fff;
  border: none;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.profile-btn--primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(11, 23, 153, 0.3);
}

.profile-btn--primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.review-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
 
.review-card__status--reported {
  background: rgba(179, 38, 30, 0.1);
  color: #b3261e;
}
 
.review-card__report-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-muted);
  font-size: 11.5px;
  font-weight: 600;
  padding: 5px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.review-card__report-btn:hover {
  background: rgba(179, 38, 30, 0.06);
  color: #b3261e;
  border-color: rgba(179, 38, 30, 0.3);
}
 
.review-card__reported-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  font-weight: 600;
  color: #b3261e;
}
 
.report-abuse-form {
  margin-top: 10px;
  padding: 14px;
  border-radius: 10px;
  background: rgba(179, 38, 30, 0.04);
  border: 1px solid rgba(179, 38, 30, 0.18);
}
 
.report-abuse-form__files {
  margin-top: 12px;
}
 
.report-abuse-form__file-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.report-abuse-form__file-chips .chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 8px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  font-size: 11.5px;
}
.report-abuse-form__file-chips .chip button {
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
  font-size: 11px;
}
.report-abuse-form__file-chips .chip button:hover {
  color: #b3261e;
}
 
.report-abuse-form__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 14px;
}
</style>